<script setup lang="ts">
const teamImageLoaded = ref(false)

useSeoMeta({
  title: "Über uns – Das Team hinter Mody",
  ogTitle: "Über uns – Das Team hinter Mody Veranstaltungstechnik",
  description: "Stefan und Franziska führen seit 2024 das Familienunternehmen Mody Veranstaltungstechnik. Über 25 Jahre Erfahrung in Bühne, Ton und Licht im Salzburger Land.",
  ogDescription: "Stefan und Franziska führen seit 2024 das Familienunternehmen Mody Veranstaltungstechnik. Über 25 Jahre Erfahrung in Bühne, Ton und Licht im Salzburger Land.",
})

const personJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Stefan Schäfer",
    "jobTitle": "Geschäftsführer",
    "worksFor": { "@type": "Organization", "name": "Mody Veranstaltungstechnik" }
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Franziska Schäfer",
    "jobTitle": "Geschäftsführerin",
    "worksFor": { "@type": "Organization", "name": "Mody Veranstaltungstechnik" }
  }
]

useHead({
  script: personJsonLd.map(ld => ({ type: 'application/ld+json', innerHTML: ld }))
})
</script>

<template>
  <main>

    <!-- Hero -->
    <div class="public-quiet-hero bg-zinc-900 py-20 px-6 text-center">
      <h1 class="text-4xl sm:text-5xl font-bold text-white mb-5">Das Duo hinter Mody</h1>
      <p class="text-stone-300 text-xl max-w-2xl mx-auto">
        Seit über 25 Jahren sorgt Mody Veranstaltungstechnik dafür, dass Firmenveranstaltungen, Hochzeiten und Events im Salzburger Land in bester Erinnerung bleiben.
      </p>
    </div>

    <!-- Team Foto + Text -->
    <div class="public-quiet-section bg-zinc-800 py-16 px-6">
      <div class="max-w-5xl mx-auto flex flex-col md:flex-row gap-10 items-center">
        <div class="team-image-shell md:w-5/12 flex-shrink-0" :aria-busy="!teamImageLoaded">
          <Skeleton v-if="!teamImageLoaded" class="team-image-skeleton" aria-hidden="true" />
          <NuxtImg
            src="/team.webp"
            alt="Stefan und Franziska – Mody Veranstaltungstechnik"
            class="team-image"
            :class="{ 'team-image--loaded': teamImageLoaded }"
            format="webp"
            quality="85"
            loading="lazy"
            @load="teamImageLoaded = true"
            @error="teamImageLoaded = true"
          />
        </div>
        <div class="md:w-7/12">
          <h2 class="text-3xl font-bold text-white mb-5">Stefan &amp; Franziska</h2>
          <p class="text-stone-300 text-lg leading-relaxed mb-5">
            Unsere Firma gibt es schon seit über 25 Jahren – und seit 2024 führen wir, Stefan und Franziska, das Familienunternehmen weiter.
          </p>
          <p class="text-stone-300 text-lg leading-relaxed mb-5">
            Mit einer Kombination aus langjähriger Erfahrung, innovativen Ideen und professionellem Equipment sind wir euer zuverlässiger Partner für jedes Event im Salzburger Land – von der unkomplizierten Hochzeit bis zur sauber geplanten Firmenveranstaltung, von Flachau bis Salzburg, von Schladming bis Tamsweg.
          </p>
          <p class="text-stone-300 text-lg leading-relaxed">
            Ob Konzert, Firmenfeier, Hochzeit oder private Veranstaltung – wir sorgen dafür, dass Technik, Ablauf und Atmosphäre perfekt harmonieren.
          </p>
        </div>
      </div>
    </div>

    <!-- Zahlen -->
    <div class="public-quiet-section bg-zinc-900 py-16 px-6">
      <div class="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
        <div>
          <div class="text-4xl font-extrabold text-[#E39737] mb-2">25+</div>
          <div class="text-stone-300 text-sm uppercase tracking-wide">Jahre Erfahrung</div>
        </div>
        <div>
          <div class="text-4xl font-extrabold text-[#4C9CD4] mb-2">500+</div>
          <div class="text-stone-300 text-sm uppercase tracking-wide">Events umgesetzt</div>
        </div>
        <div>
          <div class="text-4xl font-extrabold text-[#E39737] mb-2">200+</div>
          <div class="text-stone-300 text-sm uppercase tracking-wide">Produkte im Lager</div>
        </div>
        <div>
          <div class="text-4xl font-extrabold text-[#4C9CD4] mb-2">5★</div>
          <div class="text-stone-300 text-sm uppercase tracking-wide">Google Bewertung</div>
        </div>
      </div>
    </div>

    <!-- Werte -->
    <div class="public-quiet-section bg-zinc-800 py-16 px-6">
      <div class="max-w-5xl mx-auto">
        <h2 class="text-3xl font-bold text-white text-center mb-12">Wofür wir stehen</h2>
        <div class="grid gap-8 sm:grid-cols-3">
          <div class="flex flex-col items-center text-center">
            <i class="pi pi-clock text-4xl text-[#E39737] mb-4"></i>
            <h3 class="text-xl font-semibold text-white mb-2">Verlässlichkeit</h3>
            <p class="text-stone-300">
              Pünktlicher Aufbau, reibungsloser Ablauf. Auf uns kannst du dich verlassen – von der ersten Anfrage bis zum letzten Kabel.
            </p>
          </div>
          <div class="flex flex-col items-center text-center">
            <i class="pi pi-cog text-4xl text-[#4C9CD4] mb-4"></i>
            <h3 class="text-xl font-semibold text-white mb-2">Profi-Equipment</h3>
            <p class="text-stone-300">
              Wir setzen ausschließlich auf professionelle, regelmäßig gewartete Technik – für Klang und Licht auf höchstem Niveau.
            </p>
          </div>
          <div class="flex flex-col items-center text-center">
            <i class="pi pi-comments text-4xl text-[#E39737] mb-4"></i>
            <h3 class="text-xl font-semibold text-white mb-2">Persönliche Beratung</h3>
            <p class="text-stone-300">
              Kein Event ist wie das andere. Wir hören zu, denken mit und finden gemeinsam die beste Lösung für dein Budget und deine Vorstellung.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Servicegebiet -->
    <div class="public-quiet-section bg-zinc-900 py-16 px-6">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="text-3xl font-bold text-white mb-5">Unser Einzugsgebiet</h2>
        <p class="text-stone-300 text-lg mb-8">
          Unser Lager befindet sich in <strong class="text-white">Flachau, Ennspark 193</strong>. Von dort aus betreuen wir Events im gesamten Salzburger Land und darüber hinaus.
        </p>
        <div class="flex flex-wrap justify-center gap-3 text-sm">
          <span v-for="ort in ['Flachau', 'Altenmarkt', 'Radstadt', 'St. Johann im Pongau', 'Bischofshofen', 'Schladming', 'Tamsweg', 'St. Michael im Lungau', 'Mauterndorf', 'Hallein', 'Stadt Salzburg', 'Pongau', 'Lungau', 'Tennengau']"
            :key="ort"
            class="bg-zinc-800 text-stone-300 px-4 py-2 rounded-full">
            {{ ort }}
          </span>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="public-quiet-section bg-zinc-800 py-16 px-6 text-center">
      <h2 class="text-3xl font-bold text-white mb-4">Bereit für dein Event?</h2>
      <p class="text-stone-300 text-lg mb-8 max-w-xl mx-auto">
        Schreib uns – wir melden uns schnell zurück und finden gemeinsam die passende Lösung für Firmenveranstaltungen, Hochzeiten und regionale Events.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <Button as="router-link" to="/shop/request" class="px-8">Angebot anfordern</Button>
        <Button as="router-link" to="/contact" severity="secondary" class="px-8">Kontakt aufnehmen</Button>
      </div>
    </div>

  </main>
</template>

<style scoped lang="scss">
.team-image-shell{
  position: relative;
  min-height: 320px;
  overflow: hidden;
  border-radius: $public-radius-large;
  background: $public-surface-raised;
}

.team-image-skeleton{
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.team-image{
  display: block;
  width: 100%;
  min-height: 320px;
  object-fit: cover;
  opacity: 0;
  transition: opacity 220ms $public-ease;
}

.team-image--loaded{
  opacity: 1;
}
</style>
